import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import AmbientParticles from "@/components/site/AmbientParticles";
import StandardCTA from "@/components/site/StandardCTA";
import { RESOURCES_IMAGES } from "@/lib/site";
import { ARTICLES } from "@/data/articles";

export default function ResourcesPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setScrollY(window.scrollY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  const heroParallax = `translate3d(0, ${-scrollY * 0.14}px, 0)`;

  const onSubscribe = (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast.error("Please enter a valid business email.");
      return;
    }
    setSubscribing(true);
    // Frontend-only newsletter capture (no backend endpoint wired yet)
    setTimeout(() => {
      toast.success("You're in. Real estate intelligence is on its way.");
      setEmail("");
      setSubscribing(false);
    }, 500);
  };

  return (
    <div
      className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden"
      data-testid="resources-page"
    >
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
        <AmbientParticles count={32} tone="mixed" density={0.55} />
      </div>
      <Navbar />

      {/* ============ HERO ============ */}
      <section className="relative pt-[120px] lg:pt-[150px] pb-16 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-grid opacity-[0.06]" aria-hidden />
        <div
          className="absolute -top-32 right-0 w-[820px] h-[820px] pointer-events-none"
          style={{
            background:
              "radial-gradient(closest-side, rgba(96,165,250,0.22), transparent)",
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_1.35fr] gap-8 lg:gap-12 items-center min-h-[480px] lg:min-h-[560px]">
            {/* Left — copy */}
            <div className="fade-up relative z-10">
              <div className="eyebrow-intelligence">PERSPECTIVES</div>
              <h1 className="font-display mt-5 text-platinum text-[44px] sm:text-[56px] lg:text-[60px] xl:text-[64px] leading-[1.04] tracking-[-0.022em]">
                Intelligence for{" "}
                <span
                  className="text-sapphire-glow"
                  style={{ textShadow: "0 0 36px rgba(96,165,250,0.40)" }}
                >
                  Real Estate Leaders
                </span>
              </h1>
              <p className="mt-6 text-[16px] sm:text-[17px] text-platinum/72 leading-[1.7] max-w-[520px]">
                Perspectives on how luxury operations quietly leak revenue —
                and what serious operators do about it.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => navigate("/get-your-audit-report")}
                  data-testid="resources-hero-audit"
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                >
                  Get Your Audit Report <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate("/book-demo")}
                  data-testid="resources-hero-demo"
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2"
                >
                  Book a Demo <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Right — full-bleed hero asset */}
            <div
              className="relative lg:-mr-10 xl:-mr-20"
              data-testid="resources-hero-visual"
              style={{ transform: heroParallax, willChange: "transform" }}
            >
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(60% 55% at 55% 50%, rgba(37,99,235,0.32), transparent 72%)",
                  filter: "blur(48px)",
                }}
                aria-hidden
              />
              <div className="relative">
                <img
                  src={RESOURCES_IMAGES.hero}
                  alt="Real estate skyline wired into the Esthera intelligence network"
                  className="block w-full h-[460px] lg:h-[600px] object-cover object-center"
                  style={{
                    WebkitMaskImage:
                      "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                    maskImage:
                      "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                  }}
                  draggable="false"
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(10,15,26,0.75) 0%, rgba(10,15,26,0) 18%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.98) 100%)",
                  }}
                  aria-hidden
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(10,15,26,1) 0%, rgba(10,15,26,0.82) 14%, rgba(10,15,26,0.18) 32%, rgba(10,15,26,0) 50%, rgba(10,15,26,0) 78%, rgba(10,15,26,0.55) 100%)",
                  }}
                  aria-hidden
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ ARTICLE GRID (4 real articles) ============ */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-5 lg:gap-6">
            {ARTICLES.map((a, i) => (
              <Link
                key={a.slug}
                to={`/resources/${a.slug}`}
                data-testid={`resource-card-${a.slug}`}
                className="group relative rounded-2xl overflow-hidden border border-white/[0.07] hover:border-sapphire-glow/40 transition-all duration-500 fade-up flex flex-col"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(15,22,40,0.55) 0%, rgba(10,15,26,0.92) 100%)",
                  animationDelay: `${i * 70}ms`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 0 34px -8px rgba(96,165,250,0.42)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />

                {/* Editorial thumbnail band */}
                <div className="relative h-[140px] lg:h-[160px] overflow-hidden">
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(70% 80% at 50% 60%, rgba(37,99,235,0.32), transparent 75%)",
                      filter: "blur(28px)",
                    }}
                    aria-hidden
                  />
                  <img
                    src={RESOURCES_IMAGES.band}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1400ms] group-hover:scale-[1.05]"
                    style={{
                      mixBlendMode: "screen",
                      filter:
                        "brightness(0.92) contrast(1.65) saturate(1.15)",
                      WebkitMaskImage:
                        "radial-gradient(110% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.55) 82%, transparent 100%)",
                      maskImage:
                        "radial-gradient(110% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.55) 82%, transparent 100%)",
                    }}
                    draggable="false"
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(10,15,26,0.25) 0%, rgba(10,15,26,0) 35%, rgba(10,15,26,0) 65%, rgba(10,15,26,0.92) 100%)",
                    }}
                    aria-hidden
                  />
                </div>

                {/* Body */}
                <div className="p-6 lg:p-8 flex flex-col flex-1">
                  <h3 className="font-display text-platinum text-[22px] sm:text-[24px] leading-[1.22] tracking-[-0.014em]">
                    {a.title}
                  </h3>
                  <p className="mt-4 text-[14px] text-platinum/68 leading-[1.7] flex-1">
                    {a.excerpt}
                  </p>
                  <div className="hr-platinum mt-6 mb-4" />
                  <div className="inline-flex items-center gap-1.5 text-sapphire-glow text-[12.5px] font-medium tracking-wide group-hover:gap-3 transition-all">
                    Read the perspective <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ NEWSLETTER BAND (kept exactly as-is per founder direction) ============ */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div
            className="relative rounded-2xl border border-white/[0.08] overflow-hidden fade-up"
            style={{
              background:
                "linear-gradient(180deg, rgba(13,18,32,0.6) 0%, rgba(10,15,26,0.94) 100%)",
            }}
            data-testid="resources-newsletter"
          >
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/45 to-transparent" />
            <div className="absolute inset-0 pointer-events-none opacity-[0.55]">
              <img
                src={RESOURCES_IMAGES.band}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover object-bottom"
                style={{
                  mixBlendMode: "screen",
                  filter:
                    "brightness(0.85) contrast(1.45) saturate(1.10)",
                  WebkitMaskImage:
                    "linear-gradient(180deg, transparent 0%, #000 55%, #000 100%)",
                  maskImage:
                    "linear-gradient(180deg, transparent 0%, #000 55%, #000 100%)",
                }}
                draggable="false"
              />
            </div>

            <div className="relative grid lg:grid-cols-[1fr_1fr] gap-6 lg:gap-10 items-center p-7 lg:p-12">
              <div className="flex items-start gap-5">
                <div
                  className="w-14 h-14 shrink-0 rounded-full inline-flex items-center justify-center"
                  style={{
                    background:
                      "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.18), #0E1422 75%)",
                    border: "1px solid rgba(96,165,250,0.55)",
                    boxShadow:
                      "0 0 18px -3px rgba(96,165,250,0.45)",
                  }}
                >
                  <Mail className="w-5 h-5 text-sapphire-glow" />
                </div>
                <div>
                  <h3 className="font-display text-platinum text-[22px] sm:text-[26px] lg:text-[28px] leading-tight tracking-[-0.018em]">
                    Get Real Estate Intelligence In Your Inbox
                  </h3>
                  <p className="mt-3 text-[13.5px] text-platinum/65 leading-[1.65] max-w-[460px]">
                    Exclusive insights, research, and strategies delivered to
                    real estate leaders who want to stay ahead.
                  </p>
                </div>
              </div>

              <form
                onSubmit={onSubscribe}
                data-testid="newsletter-form"
                className="relative flex flex-col sm:flex-row items-stretch gap-2"
              >
                <input
                  type="email"
                  required
                  placeholder="Business Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="newsletter-email-input"
                  className="flex-1 h-12 px-4 rounded-md text-[14px] text-platinum placeholder:text-platinum/40 outline-none transition-colors"
                  style={{
                    background: "rgba(10,15,26,0.65)",
                    border: "1px solid rgba(96,165,250,0.25)",
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.border =
                      "1px solid rgba(96,165,250,0.65)";
                    e.currentTarget.style.boxShadow =
                      "0 0 14px -2px rgba(96,165,250,0.45)";
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.border =
                      "1px solid rgba(96,165,250,0.25)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
                <button
                  type="submit"
                  disabled={subscribing}
                  data-testid="newsletter-subscribe-btn"
                  className="btn-primary rounded-md h-12 px-6 text-[13px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {subscribing ? "Subscribing…" : "Subscribe"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <StandardCTA
        testId="resources-cta"
        eyebrow="GET STARTED"
        eyebrowTone="positioning"
        title="Ready to see where your operation leaks?"
        highlight="where your operation leaks?"
        subtitle="Every engagement begins with an Operations Audit — a precise diagnostic of how inquiries actually move through your business today."
        crystal={RESOURCES_IMAGES.ctaCrystal}
        primaryLabel="Get Your Audit Report"
        primaryHref="/get-your-audit-report"
        secondaryLabel="Book a Demo"
        secondaryHref="/book-demo"
      />

      <Footer />
    </div>
  );
}
